import React from 'react';

const ReturnPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Magic Moments AI Refund & Return Policy</h1>
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        <div className="space-y-8">
          {/* General Policy */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. General Policy</h2>
            <p className="text-gray-700">
              At Magic Moments AI, we strive to ensure you love your purchase. However, due to the custom nature of our products and services, 
              our refund and return policies vary depending on the item.
            </p>
          </section>

          {/* Physical Products */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Physical Products (Digital Frames)</h2>
            <div className="space-y-4">
              <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500">
                <h3 className="font-semibold text-blue-900 mb-3">Return Eligibility</h3>
                <p className="text-gray-700 mb-2">
                  If your physical frame arrives damaged or defective, you can request a replacement within 14 days of delivery.
                </p>
              </div>

              <div className="bg-yellow-50 p-6 rounded-lg border-l-4 border-yellow-500">
                <h3 className="font-semibold text-yellow-900 mb-3">Single Replacement Policy</h3>
                <p className="text-gray-700">
                  Each order is eligible for <strong>one replacement only</strong>. Multiple replacements for the same order are not permitted.
                </p>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-500">
                <h3 className="font-semibold text-purple-900 mb-3">Proof Required</h3>
                <p className="text-gray-700">
                  To qualify, you must provide clear photos of the defect or damage. Items showing signs of misuse or alteration will not be eligible.
                </p>
              </div>

              <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
                <h3 className="font-semibold text-green-900 mb-3">Return Shipping</h3>
                <p className="text-gray-700">
                  For verified defects, we cover the replacement shipping costs. For any other return reasons, customers are responsible for return shipping.
                </p>
              </div>
            </div>
          </section>

          {/* Digital Credits */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Digital Credits</h2>
            <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
              <h3 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                <span className="text-xl">✕</span>
                Non-Refundable
              </h3>
              <p className="text-gray-700">
                All digital credit purchases are <strong>final and non-refundable</strong> once delivered to your account. 
                If there is a delivery error, we will re-credit your account accordingly.
              </p>
            </div>
          </section>

          {/* Gifted Items */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Gifted Credits and Gifted Frames</h2>
            <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
              <h3 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                <span className="text-xl">✕</span>
                No Refunds on Gifts
              </h3>
              <p className="text-gray-700">
                Credits or digital frames gifted to another user are <strong>non-refundable once redeemed</strong>. 
                We will assist with any technical issues in gift delivery, but no cash refunds are provided.
              </p>
            </div>
          </section>

          {/* Mail-In Service */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Mail-In Service</h2>
            <div className="bg-orange-50 p-6 rounded-lg border-l-4 border-orange-500">
              <h3 className="font-semibold text-orange-900 mb-3">Service Finality</h3>
              <p className="text-gray-700 mb-3">
                Our mail-in service, where we digitize and animate your physical photos, is <strong>non-refundable once the process is complete</strong>.
              </p>
              <p className="text-gray-700">
                If the final digital product does not meet the specified order details, we will reprocess the order at no extra charge.
              </p>
            </div>
          </section>

          {/* Failed Video Generations */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Failed Video Generations Due to Content Policy Violations</h2>
            <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
              <h3 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                <span className="text-xl">⚠</span>
                No Refunds for Policy Violations
              </h3>
              <p className="text-gray-700 mb-3">
                Video generations that fail due to content that violates our Terms of Service or third-party AI generation service policies are <strong>not eligible for refunds</strong>. This includes but is not limited to:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 text-gray-700">
                <li>Prohibited or illegal content</li>
                <li>Content that violates intellectual property rights</li>
                <li>Content flagged by AI service providers as inappropriate</li>
                <li>Content containing nudity, violence, or hate speech</li>
                <li>Any content that breaches third-party service terms</li>
              </ul>
              <p className="text-gray-700 mt-3">
                While our system automatically processes refunds for technical failures, <strong>credits will not be refunded for generations that fail due to user-submitted content violating our policies or third-party service restrictions</strong>. 
              </p>
              <p className="text-gray-700 mt-2">
                We encourage all users to review our Terms of Service and ensure uploaded content complies with all applicable policies before initiating video generation.
              </p>
            </div>
          </section>

          {/* Timeframe */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Timeframe for Requests</h2>
            <div className="bg-gradient-to-r from-purple-100 to-blue-100 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3 text-xl flex items-center gap-2">
                <span className="text-2xl">⏰</span>
                14-Day Window
              </h3>
              <p className="text-gray-700">
                All refund, replacement, or reprocessing requests must be initiated within <strong>14 days of delivery</strong> of the product or service.
              </p>
            </div>
          </section>

          {/* Processing Time */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Processing Time</h2>
            <div className="bg-blue-50 p-6 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-3">5-7 Business Days</h3>
              <p className="text-gray-700">
                Once a return is approved and the item is received, please allow 5-7 business days for processing.
              </p>
            </div>
          </section>

          {/* Policy Disclosure */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Policy Disclosure</h2>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-300">
              <p className="text-gray-700">
                This policy is displayed at checkout and available on our website at all times in compliance with Virginia law. 
                By completing a purchase, you agree to these terms.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 mb-4">
              For any questions about this refund and return policy, or to initiate a return or replacement request, please contact us:
            </p>
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg border border-purple-200">
              <p className="text-gray-700"><strong>Email:</strong> <a href="mailto:support@magic-moments.ai" className="text-purple-600 hover:underline font-semibold">support@magic-moments.ai</a></p>
              <p className="text-gray-700 mt-2"><strong>Response Time:</strong> Within 24 hours on business days</p>
              <p className="text-gray-700 mt-2"><strong>Support Hours:</strong> Monday - Friday, 9 AM - 5 PM EST</p>
            </div>
          </section>

          {/* Disclaimer */}
          <section className="bg-gray-100 p-6 rounded-lg text-sm text-gray-600">
            <p className="mb-2">
              <strong>Important:</strong> Magic Moments AI reserves the right to refuse returns, replacements, or reprocessing requests that do not meet the eligibility criteria outlined in this policy. 
              This policy complies with Virginia consumer protection laws and is designed to be fair to both our customers and our business.
            </p>
            <p>
              We may update this refund and return policy from time to time. Any changes will be posted on this page with an updated "Last updated" date. 
              Your continued use of our services after any changes constitutes acceptance of the updated policy.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ReturnPolicy;
